import React, { useEffect, useMemo, useRef } from "react";
import "./OurValues.css";

import img1 from "../../../../assets/images/about/ourvalues/ov1.webp";
import img2 from "../../../../assets/images/about/ourvalues/ov2.webp";
import img3 from "../../../../assets/images/about/ourvalues/ov3.webp";
import img4 from "../../../../assets/images/about/ourvalues/ov4.webp";

const localSlides = [img1, img2, img3, img4];

const fallbackOurValues = {
  tag: "OUR VALUES",
  paragraph1:
    "IRQGYP was founded with a clear mission: to modernize Iraq's construction industry through high-quality gypsum products and reliable technical expertise. Headquartered in Baghdad with operations in Erbil, we combine advanced production facilities with a skilled local workforce to deliver solutions that meet international standards — EN and ASTM — while remaining deeply committed to Iraq's growth and development.",
  paragraph2:
    "IRQGYP was founded with a clear mission: to modernize Iraq's construction industry through high-quality gypsum products and reliable technical expertise.",
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceOurValuesPayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") {
    return tryParseJsonObject(raw) || {};
  }
  if (typeof raw !== "object") return null;

  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasMeaningfulOurValuesPayload = (c) => {
  if (!c || typeof c !== "object") return false;
  if (pickString(c.tag, c.sectionTag, c.label)) return true;
  return Boolean(
    pickString(
      c.paragraph1,
      c.body,
      c.description,
      c.text,
      c.content,
      c.intro,
      c.paragraph2,
      c.highlight,
      c.textHighlight
    )
  );
};

const OurValues = ({ content }) => {
  const sliderRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftStartRef = useRef(0);

  const view = useMemo(() => {
    const sliderSlides = localSlides.map((src) => ({ src }));

    const payload = coalesceOurValuesPayload(content);
    if (!hasMeaningfulOurValuesPayload(payload) || !payload) {
      return {
        tag: fallbackOurValues.tag,
        paragraph1: fallbackOurValues.paragraph1,
        paragraph2: fallbackOurValues.paragraph2,
        sliderSlides,
      };
    }

    const paragraph1 =
      pickString(
        payload.paragraph1,
        payload.body,
        payload.description,
        payload.text,
        payload.content,
        payload.intro
      ) || fallbackOurValues.paragraph1;
    const paragraph2 =
      pickString(payload.paragraph2, payload.highlight, payload.textHighlight, payload.summary, payload.lead) ||
      fallbackOurValues.paragraph2;

    return {
      tag: pickString(payload.tag, payload.sectionTag, payload.label) || fallbackOurValues.tag,
      paragraph1,
      paragraph2,
      sliderSlides,
    };
  }, [content]);

  const handleMouseDown = (e) => {
    const slider = sliderRef.current;
    if (!slider) return;
    isDraggingRef.current = true;
    startXRef.current = e.clientX;
    scrollLeftStartRef.current = slider.scrollLeft;
    slider.classList.add("is-grabbing");
  };

  const stopDragging = () => {
    isDraggingRef.current = false;
    sliderRef.current?.classList.remove("is-grabbing");
  };

  const handleMouseMove = (e) => {
    if (!isDraggingRef.current || !sliderRef.current) return;
    e.preventDefault();
    const walk = (e.clientX - startXRef.current) * 1.5;
    sliderRef.current.scrollLeft = scrollLeftStartRef.current - walk;
  };

  useEffect(() => {
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("blur", stopDragging);
    return () => {
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("blur", stopDragging);
    };
  }, []);

  return (
    <section className="ourvalues">
      <div className="ourvalues-container">
        <div className="ourvalues-top">
          <div className="ourvalues-left">
            <span>{view.tag}</span>
          </div>

          <div className="ourvalues-right">
            <p>{view.paragraph1}</p>

            <p className="highlight">{view.paragraph2}</p>
          </div>
        </div>

        <div
          className="ourvalues-slider"
          ref={sliderRef}
          onMouseDown={handleMouseDown}
          onMouseLeave={stopDragging}
          onMouseUp={stopDragging}
          onMouseMove={handleMouseMove}
        >
          {view.sliderSlides.map((slide, index) => (
            <div className="ourvalues-slide" key={`${slide.src}-${index}`}>
              <img src={slide.src} alt="" draggable={false} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurValues;
